import { DialogBoundary, DialogTrigger } from "@suse-o11y/dialog";
import { IconEllipsisOutlined, IconEnlarge, IconPinCrossed } from "@suse-o11y/icons";
import { isPresent } from "@suse-o11y/is-present";
import { type NonEmptyArray, isNonEmptyArray } from "@suse-o11y/non-empty-array";
import { prop } from "@suse-o11y/prop";
import { useComputed } from "@suse-o11y/signal";
import { Tooltip, TooltipBoundary, TooltipTrigger } from "@suse-o11y/tooltip";
import React from "react";

import { DropdownMenu } from "~/components/dropdown-menu";
import { DropdownMenuContent } from "~/components/dropdown-menu/content";
import { DropdownMenuItem } from "~/components/dropdown-menu/item";
import { DropdownMenuTrigger } from "~/components/dropdown-menu/trigger";

import type { DashboardWidgetSpec } from "../../../../../../common/dashboard-spec";
import { AddToDashboardDrawer } from "../../../../../../components/_add-to-dashboard-drawer";
import { OverviewWidget } from "../../../../../../components/_overview-widget";
import { WidgetHeader } from "../../../../../../components/_widget";

import { type ComponentReference, PinnedItemWidget } from "./pinned-item-widget";
import type { PinnedItemsSectionState } from "./pinned-items-section-state";

export interface PinnedItemsSectionProps<TItemData> {
	name: (count: number) => string;
	state: PinnedItemsSectionState<TItemData>;
	getCardTitle: (data: TItemData) => undefined | string;
	getCardDescription: (data: TItemData) => undefined | string;
	getCardComponentReference: (data: TItemData) => undefined | ComponentReference;
	enlargeDialog: (data: TItemData) => React.ReactNode;
	toDashboardWidgetSpec?: (item: TItemData) => DashboardWidgetSpec;
	children: (data: TItemData) => React.ReactNode;
}

export const PinnedItemsSection = <TItemData,>({
	name,
	state,
	getCardTitle,
	getCardDescription,
	getCardComponentReference,
	children,
	enlargeDialog,
	toDashboardWidgetSpec,
}: PinnedItemsSectionProps<TItemData>): null | React.ReactElement => {
	const items = useComputed(
		(monitor) =>
			state.getItems(monitor).map((item) => ({
				...item,
				dashboardWidget: toDashboardWidgetSpec?.(item.data),
			})),
		[state, toDashboardWidgetSpec],
	);

	const expanded = useComputed(state.isExpanded);

	if (items.length === 0) {
		return null;
	}

	const allDashboardWidgets = items.map(prop("dashboardWidget")).filter(isPresent);

	return (
		<OverviewWidget
			data-testid="pinned-items-section"
			header={(props) => (
				<WidgetHeader {...props} className="sticky top-0 z-20 -mb-4 rounded-none bg-grey-200 py-4">
					<DialogBoundary>
						<DropdownMenu>
							<DropdownMenuTrigger asChild={false} className="button button-xs button-square">
								<IconEllipsisOutlined className="text-xl/4" />
							</DropdownMenuTrigger>

							<DropdownMenuContent align="end">
								{isNonEmptyArray(allDashboardWidgets) && (
									<DialogTrigger asChild>
										{/* do not close the dropdown when opening the drawer */}
										<DropdownMenuItem onSelect={(event) => event.preventDefault()}>
											Add all items to dashboard
										</DropdownMenuItem>
									</DialogTrigger>
								)}

								<DropdownMenuItem onSelect={() => state.unpinAll()}>Unpin all items</DropdownMenuItem>

								<DropdownMenuItem onSelect={() => state.expandAll(false)}>Collapse all items</DropdownMenuItem>

								<DropdownMenuItem onSelect={() => state.expandAll(true)}>Expand all items</DropdownMenuItem>
							</DropdownMenuContent>
						</DropdownMenu>

						{isNonEmptyArray(allDashboardWidgets) && <AddToDashboardDrawer widgets={allDashboardWidgets} />}
					</DialogBoundary>
				</WidgetHeader>
			)}
			isExpandedByDefault={expanded}
			layout="plain"
			namespace={["pinned-list", "pinned-items"]}
			title={`${name(items.length)} (${items.length})`}
		>
			<div className="space-y-[3px]">
				{items.map(({ identifier, data, isExpanded, dashboardWidget }) => (
					<PinnedItemWidget
						actions={
							<div className="flex gap-2">
								<TooltipBoundary>
									<TooltipTrigger
										className="button button-xs button-square"
										data-beest-testid="pinned-item-unpin-button"
										onClick={() => state.unpin(data)}
									>
										<IconPinCrossed className="text-15" />
									</TooltipTrigger>

									<Tooltip className="tooltip/tooltip">Remove from pinned items</Tooltip>
								</TooltipBoundary>

								<DialogBoundary>
									<TooltipBoundary>
										<TooltipTrigger asChild>
											<DialogTrigger className="button button-xs button-square">
												<IconEnlarge />
											</DialogTrigger>
										</TooltipTrigger>

										<Tooltip className="tooltip/tooltip">Show the detailed {name(1).toLocaleLowerCase()}</Tooltip>
									</TooltipBoundary>

									{enlargeDialog(data)}
								</DialogBoundary>

								{dashboardWidget && (
									<DialogBoundary>
										<DropdownMenu>
											<DropdownMenuTrigger asChild={false} className="button button-xs button-square">
												<IconEllipsisOutlined className="text-xl/4" />
											</DropdownMenuTrigger>

											<DropdownMenuContent align="end">
												<DialogTrigger asChild>
													{/* do not close the dropdown when opening the drawer */}
													<DropdownMenuItem onSelect={(event) => event.preventDefault()}>
														Add to dashboard
													</DropdownMenuItem>
												</DialogTrigger>
											</DropdownMenuContent>
										</DropdownMenu>
										<AddToDashboardDrawer widgets={[dashboardWidget]} />
									</DialogBoundary>
								)}
							</div>
						}
						component={getCardComponentReference(data)}
						description={getCardDescription(data)}
						isExpanded={isExpanded}
						key={identifier}
						title={getCardTitle(data)}
					>
						{children(data)}
					</PinnedItemWidget>
				))}
			</div>

			{isNonEmptyArray(allDashboardWidgets) && (
				<AddToDashboardTrigger className="button button-tertiary mt-4 w-full uppercase" widgets={allDashboardWidgets}>
					Add all items to dashboard
				</AddToDashboardTrigger>
			)}
		</OverviewWidget>
	);
};

const AddToDashboardTrigger: React.FC<{
	asChild?: boolean;
	className?: string;
	widgets: NonEmptyArray<DashboardWidgetSpec>;
	children: React.ReactNode;
}> = ({ asChild, className, widgets, children }) => (
	<DialogBoundary>
		<DialogTrigger asChild={asChild} className={className}>
			{children}
		</DialogTrigger>

		<AddToDashboardDrawer widgets={widgets} />
	</DialogBoundary>
);
